import { GearboxMarketCompressorV310Abi } from '@1delta/abis'
import { MulticallRetryFunction, GenericTokenList } from '../../types'
import { buildGearboxV3PoolsCall } from './publicCallBuild'
import { getGearboxV3PoolsConverter } from './publicCallParse'
import { GearboxV3Pools } from './types'

/**
 * One-shot fetcher for all Gearbox V3 `PoolV3` passive-lender vaults on a
 * chain.
 *
 * Wraps build + multicall + parse so callers that just want "the pools
 * snapshot" don't need to hand-wire the pipeline. The passive / earn side
 * is NOT part of the lending dispatcher — it's pool-scoped while the
 * lending side is CreditManager-scoped (per-CM isolation).
 *
 * @param chainId        target chain
 * @param multicallRetry  provider-level multicall executor
 * @param prices         optional price map keyed by oracle key / underlying
 * @param tokenList      optional token list for hydrating `asset` metadata
 *
 * @returns `GearboxV3Pools` — map keyed by lowercased underlying address,
 *          or an empty object if the chain has no Gearbox resolver configured.
 */
export const fetchGearboxV3Pools = async (
  chainId: string,
  multicallRetry: MulticallRetryFunction,
  prices: { [asset: string]: number } = {},
  tokenList: GenericTokenList = {},
): Promise<GearboxV3Pools> => {
  const calls = buildGearboxV3PoolsCall(chainId)
  if (calls.length === 0) return {}

  const raw = await multicallRetry({
    chain: chainId,
    calls: calls as any,
    abi: calls.map(() => GearboxMarketCompressorV310Abi) as any,
  })

  const [converter] = getGearboxV3PoolsConverter(chainId, prices, tokenList)
  return converter(raw) ?? {}
}
